import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  adminGrowthSnapshotCreateQuestionAction,
  adminGrowthSnapshotQuestionGetAction,
  adminGrowthSnapshotStepGetAction,
  adminGrowthSnapshotUpdateQuestionAction
} from "../Action/action";

const QUESTION_TYPES = [
  { value: "single_choice", label: "Single choice" },
  { value: "multiple_choice", label: "Multiple choice" },
  { value: "text", label: "Text" }
];

const QUESTION_GROUPS = [
  { value: "checkin", label: "Check-in (Q1–Q15)" },
  { value: "looking_back", label: "Looking Back (Screen 9)" }
];

const LOOKING_BACK_ANSWER_KEYS = [
  { value: "q1_child_understanding", label: "q1 — child understanding" },
  { value: "q2_self_as_parent", label: "q2 — self as parent" },
  { value: "q3_remembered_conversation", label: "q3 — remembered conversation" },
  { value: "q4_hope_for_child", label: "q4 — hope for child" }
];

const SCORING_TYPES = [
  { value: "none", label: "None (not scored)" },
  { value: "positive", label: "Positive" },
  { value: "reverse", label: "Reverse" }
];

const CATEGORY_KEYS = [
  { value: "", label: "— None —" },
  { value: "awareness", label: "Awareness" },
  { value: "connection", label: "Connection" },
  { value: "emotional_skills", label: "Emotional Skills" },
  { value: "parent_confidence", label: "Parent Confidence" },
  { value: "family_calm", label: "Family Calm" }
];

function newOptionKey() {
  return `opt-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function emptyOptions() {
  return [
    { key: newOptionKey(), option_text: "", option_number: "1", display_order: "0", status: "1" }
  ];
}

export default function GrowthSnapshotQuestionForm({ mode = "create" }) {
  const isEdit = mode === "edit";
  const { snapshotId, sectionId, stepId, questionId } = useParams();
  const navigate = useNavigate();
  const listPath = `/growth-snapshots/${snapshotId}/sections/${sectionId}/steps/${stepId}/questions`;

  const [stepTitle, setStepTitle] = useState("");
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    question: "",
    question_type: "single_choice",
    min_selection: "",
    max_selection: "",
    is_required: 1,
    question_number: "",
    category_key: "",
    scoring_type: "none",
    include_in_analysis: 0,
    question_group: "checkin",
    answer_key: "",
    display_order: "0",
    status: 1
  });
  const [options, setOptions] = useState(emptyOptions());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await adminGrowthSnapshotStepGetAction(stepId);
        if (!cancelled && res?.success) setStepTitle(res.data?.title || "");
      } catch (e) {}
    })();
    return () => {
      cancelled = true;
    };
  }, [stepId]);

  useEffect(() => {
    if (!isEdit || !questionId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await adminGrowthSnapshotQuestionGetAction(questionId);
        if (cancelled) return;
        if (!res?.success || !res.data) {
          setError(res?.message || "Question not found.");
          return;
        }
        const d = res.data;
        const qt = d.question_type || "single_choice";
        setForm({
          question: d.question || "",
          question_type: qt,
          min_selection: d.min_selection != null ? String(d.min_selection) : "",
          max_selection: d.max_selection != null ? String(d.max_selection) : "",
          is_required: Number(d.is_required) === 0 ? 0 : 1,
          question_number: d.question_number ?? "",
          category_key: d.category_key || "",
          scoring_type: d.scoring_type || "none",
          include_in_analysis: Number(d.include_in_analysis) === 1 ? 1 : 0,
          question_group: d.question_group === "looking_back" ? "looking_back" : "checkin",
          answer_key: d.answer_key || "",
          display_order: d.display_order != null ? String(d.display_order) : "0",
          status: Number(d.status) === 0 ? 0 : 1
        });
        if (qt === "text" || d.question_group === "looking_back") {
          setOptions([]);
        } else if (Array.isArray(d.options) && d.options.length > 0) {
          setOptions(
            d.options.map((o, i) => ({
              key: o.id ? `db-${o.id}` : newOptionKey(),
              option_text: o.option_text || "",
              option_number: o.option_number != null ? String(o.option_number) : String(i + 1),
              display_order: o.display_order != null ? String(o.display_order) : String(i),
              status: Number(o.status) === 0 ? "0" : "1"
            }))
          );
        } else {
          setOptions(emptyOptions());
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || "Failed to load.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, questionId]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const onQuestionTypeChange = (value) => {
    setForm((prev) => ({
      ...prev,
      question_type: value,
      ...(value === "multiple_choice"
        ? {}
        : { min_selection: "", max_selection: "" })
    }));
    if (value === "text") {
      setOptions([]);
    } else {
      setOptions((prev) => (prev.length === 0 ? emptyOptions() : prev));
    }
  };

  const onQuestionGroupChange = (value) => {
    if (value === "looking_back") {
      setForm((prev) => ({
        ...prev,
        question_group: "looking_back",
        question_type: "text",
        min_selection: "",
        max_selection: "",
        category_key: "",
        scoring_type: "none",
        include_in_analysis: 0,
        answer_key: prev.answer_key || "q1_child_understanding"
      }));
      setOptions([]);
      return;
    }
    setForm((prev) => ({
      ...prev,
      question_group: "checkin",
      answer_key: ""
    }));
  };

  const addOptionRow = () => {
    setOptions((prev) => {
      const allFilled = prev.every((o) => String(o.option_text || "").trim().length > 0);
      if (!allFilled) {
        toast.error("Fill in option text for each row before adding another.", {
          toastId: "gsq-add-option"
        });
        return prev;
      }
      return [
        ...prev,
        {
          key: newOptionKey(),
          option_text: "",
          option_number: String(prev.length + 1),
          display_order: String(prev.length),
          status: "1"
        }
      ];
    });
  };

  const removeOptionRow = (key) => {
    setOptions((prev) => (prev.length <= 1 ? prev : prev.filter((o) => o.key !== key)));
  };

  const updateOption = (key, patch) => {
    setOptions((prev) => prev.map((o) => (o.key === key ? { ...o, ...patch } : o)));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!String(form.question || "").trim()) {
      toast.error("Question text is required", { toastId: "gs-question-form" });
      return;
    }
    const isLookingBack = form.question_group === "looking_back";
    if (isLookingBack && !String(form.answer_key || "").trim()) {
      toast.error("Looking Back questions need an answer key (q1–q4)", {
        toastId: "gs-question-form"
      });
      return;
    }
    const isMultiple = !isLookingBack && form.question_type === "multiple_choice";
    if (isMultiple) {
      const minSel =
        form.min_selection === "" || form.min_selection == null
          ? null
          : Number(form.min_selection);
      const maxSel =
        form.max_selection === "" || form.max_selection == null
          ? null
          : Number(form.max_selection);
      if (minSel != null && (!Number.isInteger(minSel) || minSel < 1)) {
        toast.error("Min selection must be a whole number of at least 1", {
          toastId: "gs-question-form"
        });
        return;
      }
      if (maxSel != null && (!Number.isInteger(maxSel) || maxSel < 1)) {
        toast.error("Max selection must be a whole number of at least 1", {
          toastId: "gs-question-form"
        });
        return;
      }
      if (minSel != null && maxSel != null && minSel > maxSel) {
        toast.error("Min selection cannot be greater than max selection", {
          toastId: "gs-question-form"
        });
        return;
      }
    }

    setSaving(true);
    try {
      const needsOptions =
        !isLookingBack &&
        (form.question_type === "single_choice" || form.question_type === "multiple_choice");
      const optionsPayload = needsOptions
        ? (options || [])
            .filter((o) => String(o.option_text || "").trim())
            .map((o, i) => ({
              option_text: String(o.option_text).trim(),
              option_number: Number(o.option_number) || i + 1,
              display_order:
                o.display_order === "" || o.display_order == null
                  ? i
                  : Number(o.display_order) || i,
              status: Number(o.status) === 0 ? 0 : 1
            }))
        : [];

      const payload = {
        question: form.question.trim(),
        question_type: isLookingBack ? "text" : form.question_type,
        min_selection: isMultiple
          ? form.min_selection === ""
            ? null
            : Number(form.min_selection)
          : null,
        max_selection: isMultiple
          ? form.max_selection === ""
            ? null
            : Number(form.max_selection)
          : null,
        is_required: Number(form.is_required) === 1 ? 1 : 0,
        question_number: form.question_number === "" ? null : Number(form.question_number),
        category_key: isLookingBack ? null : form.category_key || null,
        scoring_type: isLookingBack ? "none" : form.scoring_type || "none",
        include_in_analysis: isLookingBack ? 0 : Number(form.include_in_analysis) === 1 ? 1 : 0,
        question_group: isLookingBack ? "looking_back" : "checkin",
        answer_key: isLookingBack ? form.answer_key || null : null,
        display_order: Number(form.display_order) || 0,
        status: Number(form.status) === 1 ? 1 : 0,
        options: optionsPayload
      };

      const res = isEdit
        ? await adminGrowthSnapshotUpdateQuestionAction(questionId, payload)
        : await adminGrowthSnapshotCreateQuestionAction(stepId, payload);
      if (!res?.success) throw new Error(res?.message || "Save failed");
      toast.success(isEdit ? "Question updated" : "Question created", { toastId: "gs-question-form" });
      navigate(listPath);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Save failed", {
        toastId: "gs-question-form"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="content">
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">{error}</div>
        </div>
      ) : null}

      <div className="flex items-center justify-between" style={{ marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div className="breadcrumb" style={{ marginBottom: 0 }}>
          <Link to={listPath}>{stepTitle || "Step"}</Link>
          <span>›</span>
          <span>{isEdit ? "Edit question" : "Add question"}</span>
        </div>
        <Link className="btn btn-outline btn-sm" to={listPath}>
          ← Back
        </Link>
      </div>

      <form onSubmit={onSubmit}>
        <div className="card mb-6">
          <h2 className="section-title">Question</h2>

          <div className="form-group">
            <label>
              Question <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              value={form.question}
              onChange={(e) => setField("question", e.target.value)}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Group</label>
              <select
                className="form-control"
                value={form.question_group}
                onChange={(e) => onQuestionGroupChange(e.target.value)}
              >
                {QUESTION_GROUPS.map((g) => (
                  <option key={g.value} value={g.value}>
                    {g.label}
                  </option>
                ))}
              </select>
              <p className="small-label" style={{ marginTop: 8 }}>
                Looking Back prompts reuse this form; they are excluded from check-in scoring.
              </p>
            </div>
            {form.question_group === "looking_back" ? (
              <div className="form-group">
                <label>
                  Answer key <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  value={form.answer_key}
                  onChange={(e) => setField("answer_key", e.target.value)}
                >
                  {LOOKING_BACK_ANSWER_KEYS.map((k) => (
                    <option key={k.value} value={k.value}>
                      {k.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            <div className="form-group">
              <label>Type</label>
              <select
                className="form-control"
                value={form.question_type}
                disabled={form.question_group === "looking_back"}
                onChange={(e) => onQuestionTypeChange(e.target.value)}
              >
                {QUESTION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              {form.question_type === "text" || form.question_group === "looking_back" ? (
                <p className="small-label" style={{ marginTop: 8 }}>
                  Text questions have no predefined options; the parent types their answer in the app.
                </p>
              ) : null}
            </div>
            {form.question_group !== "looking_back" && form.question_type === "multiple_choice" ? (
              <>
                <div className="form-group">
                  <label>Min selection</label>
                  <input
                    className="form-control"
                    type="number"
                    min="1"
                    placeholder="e.g. 1"
                    value={form.min_selection}
                    onChange={(e) => setField("min_selection", e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Max selection</label>
                  <input
                    className="form-control"
                    type="number"
                    min="1"
                    placeholder="e.g. 3"
                    value={form.max_selection}
                    onChange={(e) => setField("max_selection", e.target.value)}
                  />
                </div>
              </>
            ) : null}
            {form.question_group !== "looking_back" ? (
              <div className="form-group">
                <label>Category</label>
                <select
                  className="form-control"
                  value={form.category_key}
                  onChange={(e) => setField("category_key", e.target.value)}
                >
                  {CATEGORY_KEYS.map((c) => (
                    <option key={c.value || "none"} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            <div className="form-group">
              <label>Q number</label>
              <input
                className="form-control"
                type="number"
                value={form.question_number}
                onChange={(e) => setField("question_number", e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Display order</label>
              <input
                className="form-control"
                type="number"
                min="0"
                value={form.display_order}
                onChange={(e) => setField("display_order", e.target.value)}
              />
            </div>
            
            {form.question_group !== "looking_back" ? (
              <>
                <div className="form-group">
                  <label>Scoring</label>
                  <select
                    className="form-control"
                    value={form.scoring_type}
                    onChange={(e) => setField("scoring_type", e.target.value)}
                  >
                    {SCORING_TYPES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Include in analysis</label>
                  <select
                    className="form-control"
                    value={form.include_in_analysis}
                    onChange={(e) => setField("include_in_analysis", Number(e.target.value))}
                  >
                    <option value={0}>No</option>
                    <option value={1}>Yes</option>
                  </select>
                </div>
              </>
            ) : null}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Required</label>
              <select
                className="form-control"
                value={form.is_required}
                onChange={(e) => setField("is_required", Number(e.target.value))}
              >
                <option value={1}>Yes</option>
                <option value={0}>No</option>
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                className="form-control"
                value={form.status}
                onChange={(e) => setField("status", Number(e.target.value))}
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {form.question_group !== "looking_back" && form.question_type !== "text" ? (
          <div className="card mb-6">
            <div className="flex items-center justify-between" style={{ marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
              <h2 className="section-title" style={{ margin: 0 }}>
                Answer options
              </h2>
              <button type="button" className="btn btn-outline btn-sm" onClick={addOptionRow}>
                + Add option
              </button>
            </div>
            <p className="small-label" style={{ marginBottom: 16 }}>
              Start with one option. Add the next only after the current option text is filled. Empty
              rows are skipped on save. Option number (1–4) is used for hidden scoring.
            </p>

            <div className="table-wrapper" style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>Option text</th>
                    <th style={{ width: 120 }}>Option #</th>
                    <th style={{ width: 140 }}>Display order</th>
                    <th style={{ width: 130 }}>Status</th>
                    <th style={{ width: 90 }} />
                  </tr>
                </thead>
                <tbody>
                  {options.map((o) => (
                    <tr key={o.key}>
                      <td>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Option text"
                          value={o.option_text}
                          onChange={(e) => updateOption(o.key, { option_text: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="form-control"
                          value={o.option_number}
                          onInput={(e) => {
                            const v = e.target.value.replace(/\D/g, "");
                            updateOption(o.key, { option_number: v });
                          }}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="form-control"
                          value={o.display_order}
                          onInput={(e) => {
                            const v = e.target.value.replace(/\D/g, "");
                            updateOption(o.key, { display_order: v });
                          }}
                        />
                      </td>
                      <td>
                        <select
                          className="form-control"
                          value={o.status}
                          onChange={(e) => updateOption(o.key, { status: e.target.value })}
                        >
                          <option value="1">Active</option>
                          <option value="0">Inactive</option>
                        </select>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-outline btn-sm text-danger"
                          onClick={() => removeOptionRow(o.key)}
                          disabled={options.length <= 1}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        <div className="flex gap-3">
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Update" : "Save"}
          </button>
          <Link className="btn btn-outline" to={listPath}>
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
